import { useOverlaidTooltip } from '@dealstack/frontend/shared/components/OverlaidTooltip/useOverlaidTooltip';
import { useSubstepMenu } from '@dealstack/frontend/shared/components/SubstepMenu/useSubstepMenu';
import {
	downloadInvestorsExcelTemplate,
	getDocumentWorkflowDraftTemplates,
	getMIPInvestorsDraft,
	getVestingTermsSimplified,
	updateMIPDraftInvestors,
	uploadInvestorsExcel,
} from '@dealstack/frontend/shared/dataExchange/services/mip.service';
import { downloadFileFromBlob } from '@dealstack/frontend/shared/files/downloadFile';
import { useTranslationCommonUi } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import { useRequiredParams } from '@dealstack/frontend/shared/utils/useRequiredParams';
import { I18Namespaces } from '@dealstack/shared/lib/common/i18n/i18n.ts';
import { formatUnicorn } from '@dealstack/shared/lib/format/formatUnicorn';
import { DocumentWorkflowTemplateVisibleIn } from '@dealstack/shared/lib/generated';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { TranslateFunction } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.types';
import { buildInitialContractsChecked } from './utils/buildInitialContractsChecked';

export const enum INVESTORS_SUBSTEPS {
	UPLOAD = 'UPLOAD',
	SECURITIES = 'SECURITIES',
	CONTRACTS = 'CONTRACTS',
	VESTING = 'VESTING',
}

interface Props {
	commonUiTranslation: TranslateFunction<I18Namespaces.COMMON_UI>;
	contractsEligible?: boolean;
	vestingEligible?: boolean;
}

export const toggleInvestorOnContract = (
	prevState: Map<string, Set<string>>,
	contractId: string,
	investorEmail: string
): Map<string, Set<string>> => {
	const currentSet = prevState.get(contractId);
	if (!currentSet) {
		return prevState;
	}
	const nextSet = new Set(currentSet);
	if (nextSet.has(investorEmail)) {
		nextSet.delete(investorEmail);
	} else {
		nextSet.add(investorEmail);
	}
	const newState = new Map(prevState);
	newState.set(contractId, nextSet);
	return newState;
};

export const toggleAllInvestorsOnContract = (
	prevState: Map<string, Set<string>>,
	contractId: string,
	investorEmails: string[]
): Map<string, Set<string>> => {
	const currentSet = prevState.get(contractId);
	if (!currentSet) {
		return prevState;
	}
	const allSelected = currentSet.size === investorEmails.length;
	const newState = new Map(prevState);
	newState.set(contractId, allSelected ? new Set() : new Set(investorEmails));
	return newState;
};

function createInvestorsSubsteps({
	commonUiTranslation,
	contractsEligible,
	vestingEligible,
}: Props) {
	return [
		{
			id: INVESTORS_SUBSTEPS.UPLOAD,
			name: commonUiTranslation('investors.substeps.upload'),
		},
		{
			id: INVESTORS_SUBSTEPS.SECURITIES,
			name: commonUiTranslation('investors.substeps.securities'),
		},
		...(contractsEligible
			? [
					{
						id: INVESTORS_SUBSTEPS.CONTRACTS,
						name: commonUiTranslation('investors.substeps.contracts'),
					},
				]
			: []),
		...(vestingEligible
			? [
					{
						id: INVESTORS_SUBSTEPS.VESTING,
						name: commonUiTranslation('investors.substeps.vesting'),
					},
				]
			: []),
	];
}

export const useInvestors = (
	navigate: (link: string) => void,
	refreshStatuses: () => void,
	visibleIn?: DocumentWorkflowTemplateVisibleIn[]
) => {
	const { dealId, mipId } = useRequiredParams('dealId', 'mipId');
	const commonUiTranslation = useTranslationCommonUi();

	const [investorsSubsteps, setInvestorsSubsteps] = useState(() =>
		createInvestorsSubsteps({ commonUiTranslation })
	);

	const { steps, currentStep, goNext, goToStep, setSteps, resetSubstepsState, changeStepTo } =
		useSubstepMenu({
			initialSteps: investorsSubsteps,
		});
	const { isOpen, toggleTooltip } = useOverlaidTooltip();
	const [contractsChecked, setContractsChecked] = useState<Map<string, Set<string>>>();
	const [vestingTermsChosen, setVestingTermsChosen] = useState<Map<string, string>>();
	const [eligibleSteps, setEligibleSteps] = useState<Set<string | number>>(
		new Set([INVESTORS_SUBSTEPS.UPLOAD])
	);

	const uploadInvestorsTemplateRequest = useMutation({
		mutationFn: uploadInvestorsExcel,
	});
	const downloadInvestorsExcelTemplateRequest = useMutation({
		mutationFn: downloadInvestorsExcelTemplate,
	});
	const updateInvestorsRequest = useMutation({
		mutationFn: updateMIPDraftInvestors,
	});
	const getContractsRequest = useQuery({
		queryKey: [
			formatUnicorn(
				API_PATHS.DEAL_DEALID_MIP_PROGRAMMEID_DOCUMENT_WORKFLOW_DRAFT_TEMPLATES_GET,
				{ dealId, programmeId: mipId }
			),
		],
		queryFn: () => getDocumentWorkflowDraftTemplates({ dealId, programmeId: mipId, visibleIn }),
	});
	const getVestingTermsRequest = useQuery({
		queryKey: [API_PATHS.MIP_VESTING_TERM_NAMES_GET],
		queryFn: ({ signal }) =>
			getVestingTermsSimplified(API_PATHS.MIP_VESTING_TERM_NAMES_GET, signal),
	});
	const getInvestorsDraftRequest = useQuery({
		queryKey: [API_PATHS.MIP_INVESTOR_DRAFT_GET],
		queryFn: () => getMIPInvestorsDraft(),
	});

	const contracts = getContractsRequest.data?.payload;
	const uploadedInvestors = uploadInvestorsTemplateRequest.data?.payload?.data;
	const vestingTerms = getVestingTermsRequest.data?.payload;
	const loadedInvestors = getInvestorsDraftRequest.data?.payload?.investors;

	const investors = useMemo(() => {
		if (loadedInvestors?.length && !uploadedInvestors?.length) {
			return loadedInvestors;
		} else if (uploadedInvestors?.length) {
			return uploadedInvestors;
		} else {
			return [];
		}
	}, [loadedInvestors, uploadedInvestors]);

	useEffect(() => {
		if (loadedInvestors?.length) {
			void goToStep(INVESTORS_SUBSTEPS.SECURITIES);
		}
	}, [loadedInvestors]);

	useEffect(() => {
		if (!contracts?.length || contractsChecked) {
			return;
		}
		setContractsChecked(
			buildInitialContractsChecked(contracts, loadedInvestors, !!uploadedInvestors?.length)
		);
	}, [contracts, loadedInvestors, uploadedInvestors, contractsChecked]);

	useEffect(() => {
		if (investors?.length && vestingTerms?.length) {
			if (!loadedInvestors?.length || uploadedInvestors?.length) {
				setVestingTermsChosen(
					new Map(investors.map((investor) => [investor.email, vestingTerms[0].id]))
				);
			} else {
				setVestingTermsChosen(
					new Map(
						loadedInvestors.map((investor) => [
							investor.email,
							investor?.vestingTermId ?? '',
						])
					)
				);
			}
		}
	}, [investors, vestingTerms, loadedInvestors, uploadedInvestors]);

	const substepDisableNext = currentStep?.id === INVESTORS_SUBSTEPS.UPLOAD;

	const contractsEligible = (contracts?.length ?? 0) > 0;
	const vestingEligible = !!investors?.[0]?.securities.find(
		(security) => security.isSubjectToVesting
	);

	const isCurrentStepContracts = currentStep?.id === INVESTORS_SUBSTEPS.CONTRACTS;
	const isCurrentStepVesting = currentStep?.id === INVESTORS_SUBSTEPS.VESTING;
	const isCurrentStepSecurities = currentStep?.id === INVESTORS_SUBSTEPS.SECURITIES;

	const isCurrentStepContractsAndNotVestingEligible = isCurrentStepContracts && !vestingEligible;
	const isCurrentStepSecuritiesAndNotContractsEligible =
		isCurrentStepSecurities && !contractsEligible;
	const isCurrentStepSecuritiesAndNotContractsAndNotVestingEligible =
		isCurrentStepSecurities && !contractsEligible && !vestingEligible;

	useEffect(() => {
		setEligibleSteps((prevState) => {
			const newState = new Set(prevState);

			if (investors?.length) {
				newState.add(INVESTORS_SUBSTEPS.SECURITIES);

				if (contractsEligible) {
					newState.add(INVESTORS_SUBSTEPS.CONTRACTS);
				}

				if (vestingEligible) {
					newState.add(INVESTORS_SUBSTEPS.VESTING);
				}
			}

			return newState;
		});
	}, [contractsEligible, vestingEligible, investors]);

	useEffect(() => {
		const updatedSteps = createInvestorsSubsteps({
			commonUiTranslation,
			contractsEligible,
			vestingEligible,
		});
		setInvestorsSubsteps(updatedSteps);
		setSteps(updatedSteps);
	}, [contractsEligible, vestingEligible, commonUiTranslation]);

	const goToNextSubstep = () => {
		updateInvestorsRequest.mutate(
			{
				data:
					investors?.map((investor) => ({
						...investor,
						securities: investor.securities.map((security) => ({
							...security,
							securityId: security.securityId,
						})),
						contracts:
							Array.from(contractsChecked ?? [])
								.map((contract) =>
									contract[1].has(investor.email) ? contract[0] : ''
								)
								.filter((contract) => !!contract) ?? [],
						vestingTermId: vestingTermsChosen?.get(investor.email),
					})) ?? [],
				isValid: true,
			},
			{
				onSuccess: () => {
					showToastV2({
						title: commonUiTranslation('investors.success'),
						state: 'success',
					});

					refreshStatuses();

					if (
						isCurrentStepSecuritiesAndNotContractsAndNotVestingEligible ||
						isCurrentStepContractsAndNotVestingEligible ||
						isCurrentStepVesting
					) {
						navigate('./collaborate');
					} else if (isCurrentStepSecuritiesAndNotContractsEligible) {
						void goToStep(INVESTORS_SUBSTEPS.VESTING);
					} else {
						void goNext();
					}
				},
				onError: () => {
					showToastV2({
						title: commonUiTranslation('investors.error.update'),
						state: 'error',
					});
				},
			}
		);
	};

	const resetSubsteps = useCallback(
		() => resetSubstepsState(investorsSubsteps),
		[resetSubstepsState, investorsSubsteps]
	);

	const areAllInvestorsSelected = useCallback(
		(contractId: string) => contractsChecked?.get(contractId)?.size === investors?.length,
		[investors, contractsChecked]
	);

	const onUpload = async (files: File[]) => {
		const formData = new FormData();
		formData.append('file', files[0]);
		formData.append('fileSize', files[0].size.toString());

		uploadInvestorsTemplateRequest.mutate({ formData }, { onSuccess: () => goNext() });
	};

	const onTemplateDownload = () => {
		downloadInvestorsExcelTemplateRequest.mutate(
			{},
			{
				onSuccess: (payload) => {
					if (!payload) {
						return;
					}
					const templateName = `investors-template.xlsx`;

					downloadFileFromBlob(payload, templateName);
				},
			}
		);
	};

	const onContractCheckboxClick = useCallback((contractId: string, investorId: string) => {
		setContractsChecked((prevState) =>
			prevState ? toggleInvestorOnContract(prevState, contractId, investorId) : prevState
		);
	}, []);

	const onVestingTermChange = (investorId: string) => (vestingTermId: string) => {
		setVestingTermsChosen((prevState) => {
			const newState = new Map(prevState);

			newState.set(investorId, vestingTermId);

			return newState;
		});
	};

	const onContractSelectAll = useCallback(
		(contractId: string) => {
			setContractsChecked((prevState) =>
				prevState
					? toggleAllInvestorsOnContract(
							prevState,
							contractId,
							investors?.map((investor) => investor.email) ?? []
						)
					: prevState
			);
		},
		[investors]
	);

	return {
		commonUiTranslation,
		steps,
		currentStep,
		goToNextSubstep,
		resetSubsteps,
		isOpen,
		toggleTooltip,
		onUpload,
		substepDisableNext,
		investors,
		contracts,
		vestingTerms,
		onTemplateDownload,
		contractsChecked,
		vestingTermsChosen,
		onContractCheckboxClick,
		onVestingTermChange,
		areAllInvestorsSelected,
		onContractSelectAll,
		changeStepTo,
		eligibleSteps,
		updateInvestorLoading: updateInvestorsRequest.isPending,
	};
};
